import React from "react";

import ClipboardIcon from "@src/lib/icons/18px/clipboard";
import { CONTENT_TYPE_ICON } from "@src/features/contentLibrary/contentTypeIcons";
import {
  useSidebar,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
} from "@src/lib/ui/Sidebar";
import {
  TooltipMenu,
  TooltipMenuContent,
  TooltipMenuItem,
  TooltipMenuTrigger,
} from "@src/lib/ui/TooltipMenu";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/lib/ui/Collapsible";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";

const TaskListIcon = CONTENT_TYPE_ICON.Task;

const AdminLeftNavTaskListMenuButton: React.FC = () => {
  const { pathname } = useRouter();
  const { state } = useSidebar();
  const taskLibraryItemTypeFlagEnabled = useFeatureFlag(Ff.TaskLibraryItemType);
  const auditsEnabled = useFeatureFlag(Ff.Audits);
  if (auditsEnabled) {
    const formsIsActive =
      pathname.startsWith("/library/forms") ||
      pathname.startsWith("/library/tasks");
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip="Forms"
          dataTestId="tasks-button"
          isActive={formsIsActive}
        >
          <Link href="/library/forms">
            <ClipboardIcon />
            Forms
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }
  // When audits are not enabled, "Forms" is just "Checklists" (there is only
  // one tab). The page still lives at /library/forms; /library/tasks now
  // redirects there, but we link to the canonical URL directly.
  const tasksActive =
    pathname.includes("/library/forms") || pathname.includes("/library/tasks");
  return (
    <SidebarMenuItem>
      {state === "collapsed" ? (
        <TooltipMenu>
          <TooltipMenuTrigger asChild>
            <SidebarMenuButton
              asChild
              tooltip="Checklists"
              dataTestId="tasks-button"
              isActive={tasksActive}
            >
              <Link href="/library/forms">
                <TaskListIcon />
                Checklists
              </Link>
            </SidebarMenuButton>
          </TooltipMenuTrigger>
          <TooltipMenuContent align="center" side="right">
            <TooltipMenuItem asChild>
              <Link href="/library/forms">Checklists</Link>
            </TooltipMenuItem>
            {/* Only show Alerts submenu when feature flag is enabled */}
            {taskLibraryItemTypeFlagEnabled && (
              <TooltipMenuItem asChild>
                <Link href="/library/forms/alerts">Alerts</Link>
              </TooltipMenuItem>
            )}
          </TooltipMenuContent>
        </TooltipMenu>
      ) : // When feature flag is disabled, show simple button without collapsible
      !taskLibraryItemTypeFlagEnabled ? (
        <SidebarMenuButton
          asChild
          tooltip="Checklists"
          dataTestId="tasks-button"
          isActive={tasksActive}
        >
          <Link href="/library/forms">
            <TaskListIcon />
            Checklists
          </Link>
        </SidebarMenuButton>
      ) : (
        <Collapsible className="group/collapsible" open={tasksActive}>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              asChild
              tooltip="Checklists"
              dataTestId="tasks-button"
              isActive={tasksActive}
            >
              <Link href="/library/forms">
                <TaskListIcon />
                Checklists
              </Link>
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              <SidebarMenuSubItem
                asChild
                dataTestId="alerts-button"
                isActive={pathname.includes("/library/forms/alerts")}
              >
                <Link href="/library/forms/alerts">Alerts</Link>
              </SidebarMenuSubItem>
            </SidebarMenuSub>
          </CollapsibleContent>
        </Collapsible>
      )}
    </SidebarMenuItem>
  );
};

export default AdminLeftNavTaskListMenuButton;
